import { store } from 'redux/store';
import { useHistory } from 'react-router-dom';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import errorSwal from 'utils/errorSwal';

import actionTypes from 'constants/actionTypes';

import { TradingOffer } from 'types/TradingOffer';
import { setModal } from 'modules/_shared/redux/modalActions';
import { addTradingOffer } from 'modules/trading/redux/tradingOfferActions';

import { PublishTradingOfferModalContextType } from '../types/PublishTradingOfferModalContextType';
import { publishTradingOfferModalInitValue } from '../constants/publishTradingOfferModalInitValue';
import { useGetValidityDays } from '../hooks/useGetValidityDates';

export const PublishTradingOfferModalContext =
  createContext<PublishTradingOfferModalContextType>(
    publishTradingOfferModalInitValue
  );

export const PublishTradingOfferModalProvider: FC<{
  children: ReactNode;
  tradingOffer: Partial<TradingOffer> & { className: string };
  modalActionType?: string;
  size?: string;
}> = ({ children, tradingOffer, modalActionType = actionTypes.ADD }) => {
  const { t } = useTranslate();
  const history = useHistory();

  const [isTermsAndConditionsAccepted, setIsTermsAndConditionsAccepted] =
    useState<boolean>(false);
  const [isHideSellerName, setIsHideSellerName] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [publishedOffer, setPublishedOffer] = useState<TradingOffer | null>(
    null
  );

  const { data: validityDaysData } = useGetValidityDays({
    startDate: tradingOffer.startDate,
    endDate: tradingOffer.endDate,
  });

  const handleCloseModal = () => {
    store.dispatch(setModal(null));
  };

  const handleChangeTermsAndConditionsAcceptance = () => {
    setIsTermsAndConditionsAccepted((prev) => !prev);
  };

  const handleChangeHideSellerName = () => {
    setIsHideSellerName((prev) => !prev);
  };

  const handlePublishTradingOffer = async () => {
    try {
      setIsLoading(true);

      const dispatchResult = (await store.dispatch(
        addTradingOffer({
          ...tradingOffer,
          isSellerNameHidden: isHideSellerName,
        })
      )) as TradingOffer | undefined;

      setIsLoading(false);

      if (modalActionType === actionTypes.EDIT) {
        handleCloseModal();
        history.push(`/ofertas/${tradingOffer.society}`);
        return;
      }

      // Use dispatch return value, or fallback to Redux state (thunk return may not propagate in some setups)
      const state = store.getState() as { tradingOffers?: TradingOffer[] };
      const createdOffer = dispatchResult ?? state.tradingOffers?.[0];

      if (createdOffer) {
        setPublishedOffer(createdOffer);
      } else {
        handleCloseModal();
        history.push(`/ofertas/${tradingOffer.society}`);
      }
    } catch (error) {
      setIsLoading(false);
      const errorMessage =
        modalActionType === actionTypes.EDIT
          ? t('ErrorUpdatingOffer')
          : t('ErrorPublishingTradingOffer');

      errorSwal(errorMessage);
      handleCloseModal();
    }
  };

  return (
    <PublishTradingOfferModalContext.Provider
      value={{
        data: {
          modalActionType,
          tradingOffer,
          publishedOffer,
          validityDays: validityDaysData.validityDays,
          isTermsAndConditionsAccepted,
          isHideSellerName,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeTermsAndConditionsAcceptance,
          handleChangeHideSellerName,
          handlePublishTradingOffer,
        },
      }}
    >
      {children}
    </PublishTradingOfferModalContext.Provider>
  );
};

export const usePublishTradingOfferModal = () => {
  const context = useContext(PublishTradingOfferModalContext);
  if (!context) {
    throw new Error(
      'usePublishTradingOfferModal must be used within a PublishTradingOfferModalProvider'
    );
  }
  return context;
};
